/**
 * Bulk guide import — the upload step.
 *
 * Pick a page type and an optional label, drop a set of .pdf/.docx/.txt files or a single .zip, and
 * submit. The batch is created first, then its files are staged: a zip in one request, loose
 * documents in groups of twenty (the server's cap), uploaded one group at a time with a progress
 * bar so a hundred-file batch does not fire a hundred parallel multipart bodies at the container.
 *
 * Only the staging reports are rendered, never the File objects themselves, and the reports are
 * paginated — a large batch returns a report per file, and drawing all of them at once is the thing
 * that makes an import page lock up the tab. When staging finishes, the dashboard for the batch is
 * one click away (and the page offers to go there straight away).
 */
import { SectionCard } from '@components/Common/EntityWorkspace';
import { PageHero } from '@components/Common/Panel';
import SelectFormField from '@components/Common/SelectFormField';
import FormField from '@components/Common/FormField';
import { useToast } from '@components/Common/Toast/ToastProvider';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Alert, Box, Button, LinearProgress, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FILES_PER_REQUEST, type StageReport, createBatch, uploadFiles, uploadZip } from './importApi';
import { chunk } from './batchSummary';
import {
  DOC_MAX_BYTES,
  IMPORT_ACCEPT,
  ZIP_EXT,
  ZIP_MAX_BYTES,
  type FileSelection,
  partitionImportFiles,
  rejectReasonLabel,
} from './uploadFiles';
import './import.scss';

const TYPE_OPTIONS = [
  { value: 'skill-guide', label: 'Academy guide' },
  { value: 'hiring-guide', label: 'Hiring guide' },
  { value: 'career-guide', label: 'Career guide' },
];

/** How many report rows to show per page. A batch can be hundreds, and the DOM should not be. */
const REPORTS_PER_PAGE = 25;

const humanSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

interface Progress {
  done: number;
  total: number;
}

const ImportPage: React.FC = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [pageType, setPageType] = useState<string>('');
  const [label, setLabel] = useState('');
  const [selection, setSelection] = useState<FileSelection | null>(null);
  const [dragging, setDragging] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [reports, setReports] = useState<StageReport[] | null>(null);
  const [staged, setStaged] = useState(0);
  const [failed, setFailed] = useState(0);
  const [batchId, setBatchId] = useState<string>('');
  const [reportPage, setReportPage] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);

  const onPick = useCallback(
    (list: FileList | null) => {
      if (!list || list.length === 0) return;
      const next = partitionImportFiles(Array.from(list));
      if (next.error) {
        showToast(next.error, 'error');
        return;
      }
      setSelection(next);
      // A fresh pick clears any previous run's reports.
      setReports(null);
      setBatchId('');
    },
    [showToast],
  );

  const clearSelection = () => {
    setSelection(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const selectedCount = selection?.kind === 'zip' ? 1 : selection?.files.length ?? 0;
  const canSubmit = !!pageType && selectedCount > 0 && !uploading;

  const rejectedNote = useMemo(() => {
    const rejected = selection?.rejected ?? [];
    if (!rejected.length) return null;
    const shown = rejected.slice(0, 4);
    const more = rejected.length - shown.length;
    return (
      shown.map((r) => `${r.name} (${rejectReasonLabel(r.reason, r.name.toLowerCase().endsWith(ZIP_EXT) ? ZIP_MAX_BYTES : DOC_MAX_BYTES)})`).join(', ') +
      (more > 0 ? `, and ${more} more` : '')
    );
  }, [selection]);

  const submit = async () => {
    if (!selection || !pageType) return;
    setUploading(true);
    setReports(null);
    setReportPage(0);
    try {
      const batch = await createBatch(pageType, label.trim() || undefined);
      setBatchId(batch.batchId);

      const collected: StageReport[] = [];
      let stagedTotal = 0;
      let failedTotal = 0;

      if (selection.kind === 'zip' && selection.zip) {
        setProgress({ done: 0, total: 1 });
        const result = await uploadZip(batch.batchId, selection.zip);
        collected.push(...(result.reports ?? []));
        stagedTotal += result.staged ?? 0;
        failedTotal += result.failed ?? 0;
        setProgress({ done: 1, total: 1 });
      } else {
        const groups = chunk(selection.files, FILES_PER_REQUEST);
        setProgress({ done: 0, total: groups.length });
        for (let i = 0; i < groups.length; i += 1) {
          // Sequential on purpose: one group of twenty in flight at a time keeps the batch off the
          // container's multipart limit and the browser's connection pool, the same reason the
          // résumé import uploads in a small pool rather than all at once.
          // eslint-disable-next-line no-await-in-loop
          const result = await uploadFiles(batch.batchId, groups[i], pageType);
          collected.push(...(result.reports ?? []));
          stagedTotal += result.staged ?? 0;
          failedTotal += result.failed ?? 0;
          setProgress({ done: i + 1, total: groups.length });
        }
      }

      setReports(collected);
      setStaged(stagedTotal);
      setFailed(failedTotal);
      clearSelection();
      showToast(
        `Staged ${stagedTotal} file${stagedTotal === 1 ? '' : 's'}${failedTotal ? `, ${failedTotal} failed` : ''}. Generation runs in the background.`,
        failedTotal && !stagedTotal ? 'error' : 'success',
      );
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'The import could not start. Nothing was staged.', 'error');
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  const shownReports = reports?.slice(reportPage * REPORTS_PER_PAGE, reportPage * REPORTS_PER_PAGE + REPORTS_PER_PAGE) ?? [];
  const reportPages = reports ? Math.ceil(reports.length / REPORTS_PER_PAGE) : 0;

  return (
    <Box className="cc-create guide-import-page">
      <PageHero
        kicker="Guides"
        title="Bulk import guides from documents."
        text="Drop a set of PDF, DOCX or TXT files, or one zip of them. Each file becomes a draft the AI writes in the background; track them on the batch dashboard."
        actions={
          <Button variant="outlined" startIcon={<InsightsOutlinedIcon />} onClick={() => navigate('/guides/import/batches')}>
            Past batches
          </Button>
        }
      />

      <SectionCard title="New import batch">
        <Box className="cc-field--full guide-import-page__controls">
          <SelectFormField
            required
            name="guide-import-type"
            label="Guide type"
            options={[{ value: '', label: 'Choose a type' }, ...TYPE_OPTIONS]}
            value={pageType}
            onChange={(_n, v) => setPageType(String(v))}
          />
          <FormField
            required={false}
            name="guide-import-label"
            label="Batch label (optional)"
            value={label}
            placeholder="e.g. October hiring guides"
            onChange={(_n, v) => setLabel(String(v))}
          />
        </Box>

        <Box className="cc-field--full">
          <div
            className={`guide-import-drop${dragging ? ' is-dragging' : ''}`}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              onPick(e.dataTransfer.files);
            }}
          >
            <input
              ref={inputRef}
              type="file"
              id="guide-import-input"
              accept={IMPORT_ACCEPT}
              multiple
              hidden
              onChange={(e) => onPick(e.target.files)}
            />
            <label className="guide-import-drop__label" htmlFor="guide-import-input">
              <UploadFileOutlinedIcon className="guide-import-drop__icon" />
              <Typography className="guide-import-drop__title">
                Drop files here, or <span className="guide-import-drop__link">browse</span>
              </Typography>
              <Typography className="guide-import-drop__hint">
                PDF, DOCX or TXT (many at once), or a single ZIP
              </Typography>
            </label>
          </div>

          {selection && selectedCount > 0 && (
            <Box className="guide-import-page__selected">
              <Typography className="guide-import-page__selected-count">
                {selection.kind === 'zip'
                  ? `1 zip selected: ${selection.zip?.name} (${humanSize(selection.zip?.size ?? 0)})`
                  : `${selectedCount} file${selectedCount === 1 ? '' : 's'} selected`}
              </Typography>
              <Button size="small" variant="text" startIcon={<ClearIcon />} onClick={clearSelection} disabled={uploading}>
                Clear
              </Button>
            </Box>
          )}

          {rejectedNote && (
            <Typography className="guide-import-page__skipped">
              Skipped {selection?.rejected.length}: {rejectedNote}
            </Typography>
          )}

          <Box className="guide-import-page__submit">
            <Button
              variant="contained"
              startIcon={<UploadFileOutlinedIcon />}
              disabled={!canSubmit}
              onClick={submit}
            >
              {uploading ? 'Staging…' : 'Stage import'}
            </Button>
            {!pageType && selectedCount > 0 && (
              <Typography className="guide-import-page__hint-inline">Choose a guide type first.</Typography>
            )}
          </Box>

          {progress && (
            <Box className="guide-import-page__progress">
              <Typography className="guide-import-page__progress-label">
                Staging group {progress.done} of {progress.total}…
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100)}
              />
            </Box>
          )}
        </Box>
      </SectionCard>

      {reports && (
        <SectionCard title={`Staging report — ${staged} staged${failed ? `, ${failed} failed` : ''}`}>
          <Box className="cc-field--full">
            {batchId && (
              <Alert
                className="guide-import-page__next"
                severity={failed && !staged ? 'error' : 'success'}
                action={
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => navigate(`/guides/import/batches/${batchId}`)}
                  >
                    Open dashboard
                  </Button>
                }
              >
                Files are staged. Generation runs in the background and takes a few minutes per file.
              </Alert>
            )}

            {reports.length === 0 ? (
              <Typography className="guide-import-page__empty">No files were staged.</Typography>
            ) : (
              <>
                <Box className="guide-import-reports" role="list">
                  {shownReports.map((r) => (
                    <Box key={r.fileName} className={`guide-import-reports__row is-${r.ok ? 'ok' : 'fail'}`} role="listitem">
                      <span className="guide-import-reports__icon">
                        {r.ok ? (
                          <CheckCircleOutlineRoundedIcon className="guide-import-reports__glyph--ok" />
                        ) : (
                          <ErrorOutlineRoundedIcon className="guide-import-reports__glyph--fail" />
                        )}
                      </span>
                      <span className="guide-import-reports__name" title={r.fileName}>
                        {r.fileName}
                      </span>
                      <span className="guide-import-reports__meta">
                        {r.ok ? (
                          <>
                            {typeof r.words === 'number' && <span>{r.words.toLocaleString()} words</span>}
                            {typeof r.chars === 'number' && <span>{r.chars.toLocaleString()} chars</span>}
                            {!!r.imagesFailed && (
                              <Tooltip title="Images that could not be extracted from this document">
                                <span className="guide-import-reports__warn">{r.imagesFailed} image(s) failed</span>
                              </Tooltip>
                            )}
                            {r.priorImport && (
                              <span className="guide-import-reports__dup">
                                already imported ({r.priorImport.status.toLowerCase()})
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="guide-import-reports__error">{r.error || 'could not be read'}</span>
                        )}
                      </span>
                    </Box>
                  ))}
                </Box>
                {reportPages > 1 && (
                  <Box className="guide-import-reports__pager">
                    <Button size="small" variant="outlined" disabled={reportPage === 0} onClick={() => setReportPage((p) => p - 1)}>
                      Previous
                    </Button>
                    <Typography component="span" className="guide-import-reports__pageinfo">
                      {reportPage + 1} / {reportPages}
                    </Typography>
                    <Button size="small" variant="outlined" disabled={reportPage >= reportPages - 1} onClick={() => setReportPage((p) => p + 1)}>
                      Next
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </SectionCard>
      )}
    </Box>
  );
};

export default ImportPage;
